import React, {useCallback, useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {StrictInputProps} from 'semantic-ui-react';

import {ShowcasedLink} from './context';
import * as S from './LinkModal.styles';

type LinkModalProps = {
  headerText: string;
  confirmButtonText: string;
  onSubmit: (l: ShowcasedLink) => void;
  onClose: () => void;
  existingData?: ShowcasedLink;
};

const LinkModalComp: React.FC<LinkModalProps> = ({
  headerText,
  confirmButtonText,
  onSubmit,
  onClose,
  existingData,
}) => {
  const [url, setURL] = useState(existingData?.url ?? ``);
  const [name, setName] = useState(existingData?.name ?? ``);
  const isValid = url !== `` && name !== ``;

  const [submitting, setSubmitting] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onConfirm = useCallback(async () => {
    if (!isValid) {
      toast(`Please fill out all the fields`);
      return;
    }
    setSubmitting(true);
    const linkData: ShowcasedLink = {
      url,
      name,
    };
    try {
      onSubmit(linkData);
      onClose();
    } catch (err) {
      console.error(`Failed to ${confirmButtonText.toLowerCase()}`, err);
      toast(
        `We're sorry, something went wrong. Please contact support@wandb.com to report this issue.`
      );
    } finally {
      setSubmitting(false);
    }
  }, [isValid, url, name, onSubmit, onClose, confirmButtonText]);

  return (
    <S.Modal open onClose={onClose}>
      <S.Header>{headerText}</S.Header>
      <S.Fields>
        <S.Field>
          <S.Label>Link title</S.Label>
          <S.Input
            ref={inputRef}
            placeholder="Link title"
            value={name}
            onChange={
              ((e, {value: v}) => setName(v)) as StrictInputProps['onChange']
            }
          />
        </S.Field>
        <S.Field>
          <S.Label>Link URL</S.Label>
          <S.Input
            placeholder="http://example.com"
            value={url}
            onChange={
              ((e, {value: v}) => setURL(v)) as StrictInputProps['onChange']
            }
          />
        </S.Field>
      </S.Fields>
      <S.Actions>
        <S.ConfirmButton primary loading={submitting} onClick={onConfirm}>
          {confirmButtonText}
        </S.ConfirmButton>
        <S.CancelButton onClick={onClose}>Cancel</S.CancelButton>
      </S.Actions>
    </S.Modal>
  );
};

export const LinkModal = React.memo(LinkModalComp);
